// Ayarlar → Entegrasyonlar: tek kart içinde satır satır entegrasyon listesi + paket yükseltme penceresi.
// Satırdaki "Bağlan": plan izin veriyorsa bağlama akışını başlatır (OAuth) ya da formu açar; plan izin vermiyorsa
// (Başlangıç'ta yalnızca Google Ads, Meta Ads, ChatGPT Ads, Search Console, GA4) yükseltme penceresi açılır.
// Kural backend'deki plan_limits.check_integration_connect ile aynıdır; orada da 403 ile korunur.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T } from './poyais-ui/theme';
import { useLang, addTranslations } from './i18n';
import { detectCountry } from './countries';

addTranslations({
  'Planınızı yükseltin': 'Upgrade your plan',
  'entegrasyonu Profesyonel ve üzeri planlarda kullanılabilir. Tüm platformları bağlayıp Poyais One’ın tam gücünü açın.': 'integration is available on Professional and above. Connect every platform and unlock the full power of Poyais One.',
  'Mevcut planınız': 'Your current plan',
  'Önerilen': 'Recommended',
  "Profesyonel'e Geç": 'Upgrade to Professional',
  'Mevcut plan': 'Current plan',
  'Kapat': 'Close',
  'Yönet': 'Manage',
  'Bağlan': 'Connect',
  'Bağlı': 'Connected',
  'Pasif': 'Inactive',
  'Başlangıç': 'Starter',
  'Profesyonel': 'Professional',
  'Kurumsal': 'Enterprise',
  '+ KDV /ay': '+ VAT /mo',
  '/ay': '/mo',
  'Google ağında kampanyaları ve harcamayı izleyin, performansı analiz edin.': 'Track campaigns and spend across Google’s network and analyze performance.',
  'Facebook ve Instagram reklamları, kitleler ve sayfalar.': 'Facebook & Instagram ads, audiences and pages.',
  'Web sitesi trafiği, kullanıcı davranışı, dönüşümler ve gelir (GA4).': 'Website traffic, user behavior, conversions and revenue from GA4.',
  'Organik arama tıklamaları, gösterimler, CTR ve sıralamalar.': 'Organic search clicks, impressions, CTR and rankings.',
  'OpenAI Ads API ile ChatGPT reklam kampanyalarını izleyin.': 'Track your ChatGPT advertising campaigns via the OpenAI Ads API.',
  'X (Twitter) reklam kampanyalarını ve performansı izleyin.': 'Track your X (Twitter) ad campaigns and performance.',
  'RTB House retargeting kampanyaları, kreatifler ve performans.': 'RTB House retargeting campaigns, creatives and performance.',
  'Criteo kampanyaları ve ürün bazlı performans.': 'Criteo campaigns and product-level performance.',
  'Mobil uygulama atıfı ve kampanya performansı.': 'Mobile app attribution and campaign performance.',
  'Shopify mağazanızın siparişleri ve ürünleri.': 'Orders and products from your Shopify store.',
  'Pazaryeri siparişleri, stok, iade ve kargo verileri (Pixa).': 'Marketplace orders, stock, returns and shipping data (Pixa).',
  'ikas mağazanızın ürünleri ve siparişleri.': 'Products and orders from your ikas store.',
  'Asistan, Jira görevlerini salt okunur okuyup yorumlayabilir.': 'The assistant can read your Jira issues (read-only) and interpret them.',
  'Aktif': 'Active',
});

const API = '/api/v1';

// Panelde lucide-react yok; küçük satır içi ikonlar.
type IconProps = { size?: number; style?: React.CSSProperties };
const Svg = ({ size = 16, style, children }: IconProps & { children: React.ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">{children}</svg>
);
const Lock = (p: IconProps) => <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>;
const X = (p: IconProps) => <Svg {...p}><path d="M18 6 6 18M6 6l12 12" /></Svg>;
const Check = (p: IconProps) => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>;

// Başlangıç planında olmayan (features.all_integrations isteyen) entegrasyonlar.
export const PRO_ONLY_INTEGRATIONS = ['rtbhouse', 'criteo', 'shopify', 'pixa', 'ikas', 'jira', 'adjust', 'x_ads', 'tiktok'];

export type Entitlement = {
  loading: boolean;
  isAdmin: boolean;
  planId: string;
  allIntegrations: boolean;
  plans: Record<string, any>;
  region: 'tr' | 'intl' | null;
};

export function useIntegrationEntitlement(clientId?: number): Entitlement {
  const { lang } = useLang();
  const [s, setS] = useState<Entitlement>({ loading: true, isAdmin: false, planId: 'starter', allIntegrations: false, plans: {}, region: null });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`);
        const isAdmin = me.data?.role === 'admin';
        const [st, pl] = await Promise.all([
          axios.get(`${API}/subscription/status`, { params: isAdmin || !clientId ? {} : { client_id: clientId } }),
          axios.get(`${API}/subscription/plans`),
        ]);
        const plans = pl.data?.plans || {};
        const status = st.data || {};
        // Backend get_client_plan ile aynı: aktif/deneme/iptal-dönemi dışındaki durum Başlangıç sayılır.
        const planId = ['trialing', 'active', 'canceling'].includes(status.plan_status) && plans[status.plan] ? status.plan : 'starter';
        const guess = detectCountry();
        const region: 'tr' | 'intl' =
          status.provider === 'iyzico' ? 'tr'
          : status.billing_country ? (String(status.billing_country).toUpperCase() === 'TR' ? 'tr' : 'intl')
          : guess ? (guess === 'TR' ? 'tr' : 'intl')
          : (lang === 'en' ? 'intl' : 'tr');
        if (!dead) setS({ loading: false, isAdmin, planId, allIntegrations: !!plans[planId]?.features?.all_integrations, plans, region });
      } catch {
        // Plan okunamazsa arayüz kilitlemez; backend yine 403 ile korur.
        if (!dead) setS(p => ({ ...p, loading: false, isAdmin: true }));
      }
    })();
    return () => { dead = true; };
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  return s;
}

function useNarrow(max = 640) {
  const get = () => typeof window !== 'undefined' && window.innerWidth <= max;
  const [n, setN] = useState(get);
  useEffect(() => {
    const h = () => setN(get());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return n;
}

export type IntegrationItem = {
  key: string;
  name: string;
  desc: string;
  icon: React.ReactNode;
  connected: boolean;
  enabled?: boolean;          // bağlı ama pasif ise false
  proOnly?: boolean;
  onConnect?: () => void;     // verilirse "Bağlan" doğrudan bu akışı başlatır (OAuth); verilmezse satır formu açar
  body?: React.ReactNode;     // genişleyen alan: bağlantı formu / bağlı hesap bilgisi + "Bağlantıyı Kes"
};

const goldBtn: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 11, border: 'none', background: T.accent, color: '#fff',
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap',
};
const ghostBtn: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 11, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text,
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap',
};

export function IntegrationsList({ items, ent }: { items: IntegrationItem[]; ent: Entitlement }) {
  const { t } = useLang();
  const narrow = useNarrow();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [upgradeFor, setUpgradeFor] = useState<IntegrationItem | null>(null);

  const isLocked = (it: IntegrationItem) => !ent.loading && !ent.isAdmin && !ent.allIntegrations && !!it.proOnly && !it.connected;

  const press = (it: IntegrationItem) => {
    if (isLocked(it)) { setUpgradeFor(it); return; }
    if (!it.connected && it.onConnect) { it.onConnect(); return; }
    setOpenKey(k => (k === it.key ? null : it.key));
  };

  return (
    <>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', marginTop: 16 }}>
        {items.map((it, i) => {
          const locked = isLocked(it);
          const open = openKey === it.key;
          const active = it.connected && it.enabled !== false;
          return (
            <div key={it.key} style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: narrow ? 12 : 16, padding: narrow ? '16px 16px' : '18px 22px', flexWrap: 'wrap' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: T.trackBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{it.icon}</div>
                <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: 'Manrope, sans-serif' }}>{it.name}</span>
                    {it.connected && (
                      <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', background: active ? T.greenTint : T.amberTintAlt, color: active ? T.greenText : T.amberText }}>
                        {active ? t('Bağlı') : t('Pasif')}
                      </span>
                    )}
                    {locked && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', background: T.trackBg, color: T.textMidAlt }}>
                        <Lock size={10} /> {t('Profesyonel')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: T.textMidAlt, lineHeight: 1.5, marginTop: 3 }}>{it.desc}</div>
                </div>
                <div style={{ flexShrink: 0, width: narrow ? '100%' : 'auto' }}>
                  <button onClick={() => press(it)} aria-expanded={open}
                    style={{ ...(it.connected ? ghostBtn : goldBtn), width: narrow ? '100%' : 'auto', minHeight: 40 }}>
                    {it.connected ? (open ? t('Kapat') : t('Yönet')) : t('Bağlan')}
                  </button>
                </div>
              </div>
              {/* Bağlantı kesilince satır açık kalıyordu: bağlı değilken (OAuth ile bağlanan) kartın yönetim kutusu gösterilmez. */}
              {it.body && (it.connected || !it.onConnect) && (
                <div style={{ display: open ? 'block' : 'none', padding: narrow ? '0 16px 18px' : '0 22px 20px 82px' }}>
                  <div style={{ background: T.subtleBg, border: `1px solid ${T.border}`, borderRadius: 12, padding: narrow ? 14 : 16, maxWidth: 560 }}>{it.body}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {upgradeFor && (
        <UpgradeModal
          item={upgradeFor}
          ent={ent}
          proIcons={items.filter(i => i.proOnly).map(i => ({ key: i.key, icon: i.icon }))}
          onClose={() => setUpgradeFor(null)}
        />
      )}
    </>
  );
}

function UpgradeModal({ item, ent, proIcons, onClose }: { item: IntegrationItem; ent: Entitlement; proIcons: { key: string; icon: React.ReactNode }[]; onClose: () => void }) {
  const { t, lang } = useLang();
  const narrow = useNarrow();
  const lg = lang === 'en' ? 'en' : 'tr';

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const pro = ent.plans.professional;
  const cur = ent.plans[ent.planId] || ent.plans.starter;
  const money = (p: any) =>
    ent.region === 'tr' && p?.price_try != null ? `₺${Number(p.price_try).toLocaleString('tr-TR')}`
    : p?.price_usd != null ? `$${p.price_usd}` : '';
  const perMonth = ent.region === 'tr' ? t('+ KDV /ay') : t('/ay');

  const goBilling = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'faturalandirma' }));
  };

  const bullets = (p: any, max?: number) => (p?.bullets?.[lg] || []).slice(0, max);
  const Row = ({ txt }: { txt: string }) => (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, color: T.text, lineHeight: 1.45 }}>
      <Check size={15} style={{ flexShrink: 0, marginTop: 2, color: T.accent }} /> <span>{txt}</span>
    </div>
  );

  return (
    <div onClick={onClose} role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: narrow ? 'flex-end' : 'center', justifyContent: 'center', padding: narrow ? 0 : 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.surface, width: '100%', maxWidth: 780, maxHeight: narrow ? '92vh' : '90vh', overflowY: 'auto', borderRadius: narrow ? '20px 20px 0 0' : 20, padding: narrow ? '20px 16px calc(20px + env(safe-area-inset-bottom))' : '26px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: 'Manrope, sans-serif' }}>{t('Planınızı yükseltin')}</div>
            <div style={{ fontSize: 13.5, color: T.textMidAlt, marginTop: 6, lineHeight: 1.55 }}>
              <b style={{ color: T.text }}>{item.name}</b> {t('entegrasyonu Profesyonel ve üzeri planlarda kullanılabilir. Tüm platformları bağlayıp Poyais One’ın tam gücünü açın.')}
            </div>
          </div>
          <button onClick={onClose} aria-label={t('Kapat')}
            style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer', color: T.textMidAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          {proIcons.map(p => (
            <div key={p.key} style={{ width: 38, height: 38, borderRadius: 999, border: `1px solid ${T.border}`, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>{p.icon}</div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 14, marginTop: 20 }}>
          {pro && (
            <div style={{ order: narrow ? 0 : 1, border: `2px solid ${T.accent}`, borderRadius: 16, padding: narrow ? 16 : 20, background: T.surface }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{t(pro.name)}</span>
                <span style={{ padding: '3px 10px', borderRadius: 999, background: T.accent, color: '#fff', fontSize: 10.5, fontWeight: 700 }}>{t('Önerilen')}</span>
              </div>
              {money(pro) && (
                <div style={{ marginTop: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 800, color: T.text }}>{money(pro)}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textMidAlt }}> {perMonth}</span>
                </div>
              )}
              <button onClick={goBilling} style={{ ...goldBtn, width: '100%', marginTop: 14, minHeight: 44 }}>{t("Profesyonel'e Geç")}</button>
              {pro.plus_label?.[lg] && <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, margin: '16px 0 10px' }}>{pro.plus_label[lg]}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: pro.plus_label?.[lg] ? 0 : 16 }}>
                {bullets(pro).map((b: string) => <Row key={b} txt={b} />)}
              </div>
            </div>
          )}
          {cur && (
            <div style={{ order: narrow ? 1 : 0, border: `1px solid ${T.border}`, borderRadius: 16, padding: narrow ? 16 : 20, background: T.surface }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{t(cur.name)}</span>
                <span style={{ padding: '3px 10px', borderRadius: 999, background: T.trackBg, color: T.textMidAlt, fontSize: 10.5, fontWeight: 700 }}>{t('Mevcut planınız')}</span>
              </div>
              {money(cur) && (
                <div style={{ marginTop: 10 }}>
                  <span style={{ fontSize: 30, fontWeight: 800, color: T.text }}>{money(cur)}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textMidAlt }}> {perMonth}</span>
                </div>
              )}
              <button disabled style={{ ...ghostBtn, width: '100%', marginTop: 14, minHeight: 44, opacity: 0.6, cursor: 'default' }}>{t('Mevcut plan')}</button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 16 }}>
                {bullets(cur, 6).map((b: string) => <Row key={b} txt={b} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
